/*global chrome */
import { Grid, Stack, Button, Backdrop, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  loginValidateMFA,
  loginUser,
  resendActivationLink,
  getWorkspaceUser,
  amplitude,
} from 'wisestep-api';
import {
  LoginForm as Form,
  WsTypography,
  WsTypoColors,
  WsTypoVariants,
  LoginAlert,
  MfaForm,
  TextOnLine,
  WsColors,
  Item,
  WisestepLogo,
} from 'wisestep-base';

import { WsLink, AuthLayout } from '../../../components';
import { BLOCKED_USERS } from '../../../utils/Constants';
import { getSocialIcon } from '../../../utils/getSocialIcon';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { getAuthToken } from '../../../utils/token';
import useLoginFormStore from '../store/useLoginFormStore';
import ILoginParams from '../types/ILoginParams';
import { socialSignIn } from '../utils/common';

const LoginButton = styled(Button)(() => ({
  color: WsColors.GenericRichBlack,
}));

const LoginForm = () => {
  const navigate = useNavigate();

  const mfaToken = useLoginFormStore((state) => state.mfaToken);

  const updateMfaToken = useLoginFormStore((state) => state.updateMfaToken);

  const [twoFARequired, setTwoFARequired] = useState<boolean>(false);

  const [errorElement, setErrorElement] = useState<React.ReactElement>();

  const setNotification = useNotificationStore((state) => state.setNotification);

  const [isLoading, setIsLoading] = useState(false);

  const [params] = useSearchParams();

  const ForgotPassword = () => (
    <WsLink to="/forgot-password">
      <WsTypography
        color={WsTypoColors.PRIMARY}
        cursor="pointer"
        variant={WsTypoVariants.BODY1}
        onClick={() =>
          amplitude('Forgot Password', {
            clickedOn: 'Forgot_Password',
            pageName: 'Login',
            pageUrl: window.location.href,
            interfaceType: 'Web',
          })
        }
      >
        Forgot Password
      </WsTypography>
    </WsLink>
  );

  const overRideToken = (data: ILoginParams) => {
    data.overrideExistingToken = true;
    onSubmit(data);
  };

  const activateMFA = (token: string) => {
    const bearerToken = token;
    if (bearerToken && bearerToken.slice(0, 7) === 'Bearer ') {
      updateMfaToken(bearerToken.slice(7, bearerToken.length));
    }
    setTwoFARequired(true);
  };

  const resendEmail = async (email: string) => {
    resendActivationLink(email).then(() => {
      setNotification({
        message: 'Verification email resent. Please verify and login again.',
        type: 'success',
      });
    });
  };

  const handleMFA = (mfa: string) => {
    const data = {
      code: mfa,
      temporaryToken: mfaToken,
    };
    if (data) {
      loginValidateMFA(data)
        .then(() => navigate('/workspace/initiate'))
        .catch(() => {
          setNotification({
            message: 'Failed to sign in! Invalid MFA code. Please enter a valid code',
            type: 'error',
          });
        });
    }
  };
  const amplitudeEvent = {
    404: () =>
      amplitude('Email Not registered', {
        clickedOn: 'Login_Failed_Emailnotregistered',
        pageName: 'Login',
        pageUrl: window.location.href,
        interfaceType: 'Web',
      }),
    500: () =>
      amplitude('Login Failed', {
        clickedOn: 'Login_Failed',
        pageName: 'Login',
        pageUrl: window.location.href,
        interfaceType: 'Web',
      }),
    401: () =>
      amplitude('Incorrect credentials', {
        clickedOn: 'Login_Failed_Incorrect_Credentials',
        pageName: 'Login',
        pageUrl: window.location.href,
        interfaceType: 'Web',
      }),
  };
  const onSubmit = (loginParam: ILoginParams) => {
    if (BLOCKED_USERS.includes(loginParam.username)) {
      setErrorElement(
        <LoginAlert
          loginParam={loginParam}
          bearerToken=""
          statusCode={44444}
          resendEmail={resendEmail}
          overRideToken={overRideToken}
          activateMFA={activateMFA}
        />
      );
    } else {
      setIsLoading(true);
      loginUser({
        ...loginParam,
        loginUsing: 'website',
        overrideExistingToken: loginParam.overrideExistingToken,
        rememberMe: true,
      })
        .then(async (d) => {
          const userCheck = await getWorkspaceUser();
          if (userCheck.data != undefined) {
            if (
              userCheck.data.authorities.includes('ROLE_ADMIN') ||
              userCheck.data.authorities.includes('ROLE_SUPER_ADMIN')
            ) {
              if (params.get('app') == 'extension' && params.get('extensionId')) {
                /* eslint-disable */
              chrome.runtime.sendMessage(params.get("extensionId")?.toString(), {
                type: 'authtoken',
                token:
                  userCheck.data.authorization ||
                  localStorage.authenticationToken ||
                  sessionStorage.authenticationToken|| d.data['id_token'],
              });
              
              window.close();
              navigate('/', {
                state: {
                  isAdmin: true,
                },
              });
            }else if(params.get('app') == "ignite"){
              window.open(`ignite://action=login&token=${d.data['id_token'] ?? getAuthToken()}`);
              window.close();
          }else{
              navigate('/', {
                state: {
                  isAdmin: true,
                },
              });
            }
              /* eslint-enable */
            } else {
              /* eslint-disable */
              if (params.get('app') == 'extension' && params.get("extensionId")) {
                chrome.runtime.sendMessage(params.get("extensionId")?.toString(), {
                  type: 'authtoken',
                token:
                  userCheck.data.authorization ||
                  localStorage.authenticationToken ||
                  sessionStorage.authenticationToken || d.data['id_token'],
              });
              window.close();
              navigate('/workspace/initiate');
            }else if(params.get('app') == "ignite"){
              window.open(`ignite://action=login&token=${d.data['id_token'] ?? getAuthToken()}`);
              window.close();
          }else{
              navigate('/workspace/initiate');
            }
              /* eslint-enable */
            }
          }
        })
        .catch((error) => {
          setIsLoading(false);
          const bearerToken =
            error.response.status === 428 ? error.response.headers.authorization : null;
          setErrorElement(
            <LoginAlert
              loginParam={loginParam}
              bearerToken={bearerToken}
              statusCode={error.response.status}
              resendEmail={resendEmail}
              overRideToken={overRideToken}
              activateMFA={activateMFA}
            />
          );
          amplitudeEvent[error.response.status]();
        });
    }
  };

  return (
    <AuthLayout>
      <Item>
        <Stack alignItems="center" spacing={4} direction="column">
          <WisestepLogo />
          <WsTypography variant={WsTypoVariants.TITLE}>Sign in to Wisestep</WsTypography>
        </Stack>
        <Grid mt={3} container justifyContent="center" alignItems="center">
          <Grid item>
            <Item>
              <LoginButton
                onClick={() => {
                  socialSignIn('Google');
                  amplitude('Login_using_Google', {
                    clickedOn: 'Login using Google',
                    pageName: 'Login',
                    pageUrl: window.location.href,
                    interfaceType: 'Web',
                  });
                }}
                startIcon={getSocialIcon('Google')}
                variant="outlined"
                size="large"
              >
                Sign in with Google
              </LoginButton>
            </Item>
          </Grid>
          <Grid item>
            <Item>
              <LoginButton
                onClick={() => {
                  socialSignIn('Outlook');
                  amplitude('Login_using_Microsoft', {
                    clickedOn: 'Login using Microsoft',
                    pageName: 'Login',
                    pageUrl: window.location.href,
                    interfaceType: 'Web',
                  });
                }}
                startIcon={getSocialIcon('Microsoft')}
                variant="outlined"
                size="large"
              >
                Sign in with Microsoft
              </LoginButton>
            </Item>
          </Grid>
        </Grid>
        <TextOnLine>OR</TextOnLine>
        {twoFARequired ? (
          <MfaForm handleMFA={handleMFA} />
        ) : (
          <Form
            forgotPassword={<ForgotPassword />}
            onSubmit={onSubmit}
            errorElement={errorElement}
            amplitudeHandler={() =>
              amplitude('Login_using_Email', {
                clickedOn: 'Login using Email',
                pageName: 'Login',
                pageUrl: window.location.href,
                interfaceType: 'Web',
              })
            }
          />
        )}
        {isLoading && (
          <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
        <Stack justifyContent="center" alignItems="center" direction="row" spacing={1}>
          <WsTypography variant={WsTypoVariants.BODY1}>Don’t have an account yet ?</WsTypography>
          <WsLink to="sign-up">
            <WsTypography
              variant={WsTypoVariants.BODY1}
              color={WsTypoColors.PRIMARY}
              cursor="pointer"
              onClick={() =>
                amplitude('Signup using Email', {
                  clickedOn: 'Signup_Email',
                  pageName: 'Signup',
                  pageUrl: window.location.href,
                  interfaceType: 'Web',
                })
              }
            >
              Sign up here
            </WsTypography>
          </WsLink>
        </Stack>
      </Item>
    </AuthLayout>
  );
};

export default LoginForm;
